/**
 * MusicKit JS Service
 *
 * Handles initialization, authorization, search, and library operations
 * for the Apple Music API via MusicKit JS.
 *
 * @see https://developer.apple.com/documentation/musickitjs
 * @see https://developer.apple.com/documentation/applemusicapi
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

let musicKitInstance = null;
let initPromise = null;

/**
 * Wait for the MusicKit global object to be available
 * @returns {Promise<void>}
 */
function waitForMusicKit() {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (typeof MusicKit !== 'undefined') {
      resolve();
      return;
    }

    // Wait for the musickitloaded event
    const timeout = setTimeout(() => {
      reject(new Error('MusicKit JS failed to load within 10 seconds'));
    }, 10000);

    document.addEventListener('musickitloaded', () => {
      clearTimeout(timeout);
      resolve();
    }, { once: true });
  });
}

/**
 * Initialize MusicKit with developer token from backend
 *
 * This function:
 * 1. Waits for MusicKit JS to load
 * 2. Fetches the developer token from our backend
 * 3. Configures MusicKit with the token
 * 4. Returns the MusicKit instance
 *
 * @returns {Promise<MusicKit.MusicKitInstance>}
 */
export async function initializeMusicKit() {
  // Return cached instance if available
  if (musicKitInstance) {
    return musicKitInstance;
  }

  // Return existing initialization promise to prevent duplicate init
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    // console.log('[MusicKit] Waiting for MusicKit JS to load...');
    await waitForMusicKit();
    // console.log('[MusicKit] MusicKit JS loaded');

    // Fetch developer token from our backend
    // console.log('[MusicKit] Fetching developer token from backend...');
    const response = await fetch(`${API_URL}/api/token`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Failed to fetch developer token: ${error.error || response.statusText}`);
    }

    const { token } = await response.json();
    // console.log('[MusicKit] Developer token received');

    // Configure MusicKit
    // @ts-ignore - MusicKit is a global
    await MusicKit.configure({
      developerToken: token,
      app: {
        name: 'Apple Music Importer',
        build: '1.0.0',
      },
    });

    // @ts-ignore - MusicKit is a global
    musicKitInstance = MusicKit.getInstance();
    console.log('[MusicKit] Initialized'); // Keep one summary log

    return musicKitInstance;
  })();

  return initPromise;
}

/**
 * Get the MusicKit instance (must be initialized first)
 * @returns {MusicKit.MusicKitInstance | null}
 */
export function getMusicKitInstance() {
  return musicKitInstance;
}

/**
 * Authorize user with Apple Music
 *
 * Opens Apple's OAuth popup for user to sign in and grant access.
 * The user must have an active Apple Music subscription.
 *
 * @returns {Promise<boolean>} True if authorization succeeded
 * @throws {Error} If authorization fails or is denied
 */
export async function authorize() {
  const music = await initializeMusicKit();

  if (music.isAuthorized) {
    console.log('[MusicKit] Already authorized');
    return true;
  }

  try {
    console.log('[MusicKit] Opening authorization popup...');
    await music.authorize();
    console.log('[MusicKit] User authorized successfully');
    return true;
  } catch (error) {
    console.error('[MusicKit] Authorization failed:', error);
    throw new Error(
      error.message || 'Apple Music authorization was denied or failed'
    );
  }
}

/**
 * Check if user is currently authorized
 * @returns {Promise<boolean>}
 */
export async function isAuthorized() {
  try {
    const music = await initializeMusicKit();
    return music.isAuthorized;
  } catch {
    return false;
  }
}

/**
 * Sign out the user (revoke authorization)
 * @returns {Promise<void>}
 */
export async function unauthorize() {
  const music = await initializeMusicKit();
  await music.unauthorize();
  console.log('[MusicKit] User signed out');
}

/**
 * Search Apple Music catalog for songs
 *
 * @param {string} query - Search query (typically "title artist")
 * @param {number} limit - Maximum results to return (1-25)
 * @returns {Promise<Array>} Array of song objects from Apple Music catalog
 */
export async function searchSongs(query, limit = 10) {
  const music = await initializeMusicKit();

  if (!query || query.trim().length === 0) {
    return [];
  }

  try {
    // Use user's storefront dynamically (defaults to 'us' if not available)
    const storefront = music.storefrontId || 'us';

    const response = await music.api.music(`/v1/catalog/${storefront}/search`, {
      term: query.trim(),
      types: 'songs',
      limit: Math.min(limit, 25), // Apple max is 25
    });

    const songs = response?.data?.results?.songs?.data || [];
    console.log(`[MusicKit] Search "${query.substring(0, 30)}..." -> ${songs.length} results (storefront: ${storefront})`);

    // Log song IDs for debugging
    if (songs.length > 0) {
      console.log(`[MusicKit] Top result ID: ${songs[0].id} - "${songs[0].attributes?.name}" by ${songs[0].attributes?.artistName}`);
    }

    return songs;
  } catch (error) {
    console.error('[MusicKit] Search failed:', error);

    // Handle specific error types
    if (error.status === 401) {
      throw new Error('Authorization expired. Please sign in again.');
    }
    if (error.status === 429) {
      throw new Error('Rate limit exceeded. Please wait a moment and try again.');
    }

    return [];
  }
}

/**
 * Add songs to user's Apple Music library
 *
 * Uses the Apple Music API to add catalog songs to the user's
 * iCloud Music Library.
 *
 * @param {string[]} songIds - Array of Apple Music catalog song IDs
 * @returns {Promise<boolean>} True if all songs were added successfully
 * @throws {Error} If user is not authorized or API call fails
 *
 * @see https://developer.apple.com/documentation/applemusicapi/add_a_resource_to_a_library
 */
export async function addToLibrary(songIds) {
  const music = await initializeMusicKit();

  if (!music.isAuthorized) {
    throw new Error('User not authorized. Please connect to Apple Music first.');
  }

  if (!songIds || songIds.length === 0) {
    console.log('[MusicKit] No songs to add');
    return true;
  }

  try {
    // Apple Music API requires song IDs as query parameters
    // Format: POST /v1/me/library?ids[songs]=id1,id2,id3
    const idsParam = songIds.map(id => String(id)).join(',');

    // Make the API call to add songs
    await music.api.music(`/v1/me/library?ids[songs]=${idsParam}`, null, {
      fetchOptions: {
        method: 'POST',
      },
    });

    return true;
  } catch (error) {
    console.error('[MusicKit] addToLibrary failed:', error);

    // Handle specific error types
    if (error.status === 401) {
      throw new Error('Authorization expired. Please sign in again.');
    }
    if (error.status === 403) {
      throw new Error('Permission denied. Make sure you have an Apple Music subscription.');
    }
    if (error.status === 429) {
      throw new Error('Rate limit exceeded. Please wait and try again.');
    }

    throw new Error(`Failed to add songs to library: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Add songs to library in batches with rate limiting
 *
 * Apple recommends batching library additions. This function
 * splits large lists into batches and adds delays between them.
 *
 * @param {string[]} songIds - Array of song IDs to add
 * @param {object} options - Options for batch processing
 * @param {number} options.batchSize - Songs per batch (default: 25)
 * @param {number} options.delayMs - Delay between batches in ms (default: 200)
 * @param {function} options.onProgress - Progress callback
 * @returns {Promise<{success: number, failed: number}>}
 */
export async function addToLibraryBatched(songIds, options = {}) {
  const {
    batchSize = 25,
    delayMs = 200,
    onProgress,
  } = options;

  let success = 0;
  let failed = 0;

  for (let i = 0; i < songIds.length; i += batchSize) {
    const batch = songIds.slice(i, i + batchSize);

    try {
      await addToLibrary(batch);
      success += batch.length;
    } catch (error) {
      console.error(`[MusicKit] Batch ${Math.floor(i / batchSize) + 1} failed:`, error);
      failed += batch.length;
    }

    if (onProgress) {
      onProgress({
        processed: i + batch.length,
        total: songIds.length,
        success,
        failed,
      });
    }

    // Add delay between batches to respect rate limits
    if (i + batchSize < songIds.length) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  return { success, failed };
}

/**
 * Create a new playlist in the user's library
 *
 * @param {string} name - Playlist name
 * @param {string} description - Playlist description (optional)
 * @returns {Promise<string>} The ID of the created playlist
 */
export async function createPlaylist(name, description = '') {
  const music = await initializeMusicKit();

  if (!music.isAuthorized) {
    throw new Error('User not authorized');
  }

  const response = await music.api.music('/v1/me/library/playlists', null, {
    fetchOptions: {
      method: 'POST',
      body: JSON.stringify({
        attributes: {
          name,
          description: description || `Created by Apple Music Importer on ${new Date().toLocaleDateString()}`,
        },
      }),
    },
  });

  const playlistId = response?.data?.data?.[0]?.id;
  console.log(`[MusicKit] Created playlist "${name}" with ID: ${playlistId}`);

  return playlistId;
}

/**
 * Add songs to an existing playlist
 *
 * @param {string} playlistId - The library playlist ID
 * @param {string[]} songIds - Array of catalog song IDs to add
 * @returns {Promise<boolean>}
 */
export async function addSongsToPlaylist(playlistId, songIds) {
  const music = await initializeMusicKit();

  if (!music.isAuthorized) {
    throw new Error('User not authorized');
  }

  await music.api.music(`/v1/me/library/playlists/${playlistId}/tracks`, null, {
    fetchOptions: {
      method: 'POST',
      body: JSON.stringify({
        data: songIds.map(id => ({
          id: String(id),
          type: 'songs',
        })),
      }),
    },
  });

  console.log(`[MusicKit] Added ${songIds.length} songs to playlist ${playlistId}`);
  return true;
}

/**
 * Add songs to a playlist in batches with rate limiting
 *
 * @param {string} playlistId - The library playlist ID
 * @param {string[]} songIds - Array of song IDs to add
 * @param {object} options - Options for batch processing
 * @param {number} options.batchSize - Songs per batch (default: 50)
 * @param {number} options.delayMs - Delay between batches in ms (default: 200)
 * @param {function} options.onProgress - Progress callback
 * @returns {Promise<{success: number, failed: number}>}
 */
export async function addSongsToPlaylistBatched(playlistId, songIds, options = {}) {
  const {
    batchSize = 50,
    delayMs = 200,
    onProgress,
  } = options;

  let success = 0;
  let failed = 0;

  for (let i = 0; i < songIds.length; i += batchSize) {
    const batch = songIds.slice(i, i + batchSize);

    try {
      await addSongsToPlaylist(playlistId, batch);
      success += batch.length;
    } catch (error) {
      console.error(`[MusicKit] Playlist batch ${Math.floor(i / batchSize) + 1} failed:`, error);
      failed += batch.length;
    }

    if (onProgress) {
      onProgress({
        processed: i + batch.length,
        total: songIds.length,
        success,
        failed,
      });
    }

    // Add delay between batches
    if (i + batchSize < songIds.length) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  return { success, failed };
}
